import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2, PackageCheck } from "lucide-react";
import { api, formatINR } from "@/lib/api";

const CodConfirm = () => {
    const [params] = useSearchParams();
    const orderNumber = params.get("order") || "";
    const token = params.get("token") || "";
    const [state, setState] = useState({ loading: true, ok: false, alreadyConfirmed: false, total: 0, error: "" });

    useEffect(() => {
        if (!orderNumber || !token) {
            setState({ loading: false, ok: false, error: "Invalid confirmation link" });
            return;
        }
        (async () => {
            try {
                const { data } = await api.get(`/orders/cod/confirm`, { params: { order: orderNumber, token } });
                setState({ loading: false, ok: true, alreadyConfirmed: !!data.already_confirmed, total: data.total_inr || 0 });
            } catch (e) {
                setState({ loading: false, ok: false, error: e.response?.data?.detail || "Confirmation failed" });
            }
        })();
    }, [orderNumber, token]);

    return (
        <div className="max-w-xl mx-auto px-6 py-20" data-testid="cod-confirm-page">
            <div className="rounded-3xl glass p-10 text-center">
                {state.loading && (
                    <>
                        <Loader2 className="w-10 h-10 text-primary animate-spin mx-auto" />
                        <div className="mt-4 text-sm text-muted-foreground">Confirming your order…</div>
                    </>
                )}
                {!state.loading && state.ok && (
                    <>
                        <div className="w-16 h-16 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center mx-auto">
                            {state.alreadyConfirmed ? <PackageCheck className="w-8 h-8" /> : <CheckCircle2 className="w-8 h-8" />}
                        </div>
                        <h1 className="font-display text-3xl font-bold mt-4" data-testid="cod-confirm-title">
                            {state.alreadyConfirmed ? "Already confirmed 💛" : "Order confirmed 🎉"}
                        </h1>
                        <p className="text-sm text-muted-foreground mt-2">
                            Order <strong>{orderNumber}</strong>{state.total ? ` — ₹${state.total.toLocaleString()} on delivery` : ""}.
                            Our team will call to schedule your dispatch shortly.
                        </p>
                        <div className="flex gap-3 justify-center mt-6">
                            <Link to={`/track?number=${orderNumber}`} className="rounded-full bg-primary text-primary-foreground px-5 py-2 text-sm font-semibold" data-testid="cod-confirm-track-btn">Track this order</Link>
                            <Link to="/shop" className="rounded-full border px-5 py-2 text-sm">Keep shopping</Link>
                        </div>
                    </>
                )}
                {!state.loading && !state.ok && (
                    <>
                        <div className="w-16 h-16 rounded-full bg-rose-100 text-rose-700 flex items-center justify-center mx-auto">
                            <XCircle className="w-8 h-8" />
                        </div>
                        <h1 className="font-display text-3xl font-bold mt-4" data-testid="cod-confirm-error">Couldn't confirm</h1>
                        <p className="text-sm text-muted-foreground mt-2">{state.error}</p>
                        <div className="flex gap-3 justify-center mt-6">
                            <Link to="/contact" className="rounded-full bg-primary text-primary-foreground px-5 py-2 text-sm font-semibold">Contact support</Link>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default CodConfirm;
